import { TeacherJSON, TeacherParametricJSON, TeacherActionStepsJSON } from './JSONSchema';
import { GeometryEngine } from '../core/GeometryEngine';
import { ConstructionRegistry } from '../registry/ConstructionRegistry';
import { ConstructionStep } from '../types';
import { Point } from '../objects/Point';
import { Segment } from '../objects/Segment';
import { Circle } from '../objects/Circle';
import { Arc } from '../objects/Arc';
import { Label } from '../objects/Label';
import { DrawPoint, DrawLine, DrawCircle, DrawArc } from '../animations/DrawPoint';
import { MoveCompass, Fade } from '../animations/MoveCompass';
import { SpeechManager } from '../utils/SpeechManager';

export class JSONParser {
  public static parse(json: TeacherJSON, engine: GeometryEngine): ConstructionStep[] {
    let steps: ConstructionStep[] = [];

    if ('construction' in json && json.construction) {
      steps = this.parseParametricJSON(json as TeacherParametricJSON, engine);
    } else if ('steps' in json && Array.isArray(json.steps)) {
      const firstStep = json.steps[0];
      if (firstStep && firstStep.actions) {
        steps = this.parseActionStepsJSON(json as TeacherActionStepsJSON, engine);
      } else if ('construction' in json && (json as TeacherParametricJSON).construction) {
        steps = this.parseParametricJSON(json as TeacherParametricJSON, engine);
      }
    }

    if (steps.length === 0) {
      throw new Error('Invalid Teacher JSON: Must contain either a "construction" name or a "steps" action array.');
    }

    // Handle Pre-Step Introduction / Overview Voiceover
    const introText = json.introduction || json.introNarration;
    if (introText) {
      const estIntroMs = SpeechManager.getInstance().estimateDurationMs(introText);
      const introStep: ConstructionStep = {
        id: 'step_intro',
        title: 'Lesson Introduction',
        description: json.description || json.title,
        narration: introText,
        duration: estIntroMs,
        animations: [],
      };
      if (json.introVoiceoverAudioUrl) {
        (introStep as any).voiceoverAudioUrl = json.introVoiceoverAudioUrl;
      }
      steps.unshift(introStep);
    }

    return steps;
  }

  private static parseParametricJSON(json: TeacherParametricJSON, engine: GeometryEngine): ConstructionStep[] {
    const ctor = ConstructionRegistry.get(json.construction);
    if (!ctor) {
      throw new Error(`Unknown construction type '${json.construction}'. Registered types: ${ConstructionRegistry.listAll().map(c => c.id).join(', ')}`);
    }

    const builder = new ctor(engine.renderer, engine.compass, engine.ruler);
    const steps = builder.buildSteps(json.params || {});

    // Apply teacher step overrides (titles, narrations, audio URLs, durations)
    const stepOverrides = json.steps || json.customSteps;
    if (stepOverrides && Array.isArray(stepOverrides)) {
      stepOverrides.forEach((override, idx) => {
        const targetStepIdx = override.step ? override.step - 1 : idx;
        const targetStep = steps[targetStepIdx];

        if (targetStep) {
          if (override.title) targetStep.title = override.title;
          if (override.description) targetStep.description = override.description;
          if (override.narration) targetStep.narration = override.narration;
          if (override.voiceoverAudioUrl || override.narrationAudioUrl) {
            (targetStep as any).voiceoverAudioUrl = override.voiceoverAudioUrl || override.narrationAudioUrl;
          }
          if (override.duration) targetStep.duration = override.duration;
        }
      });
    }

    return steps;
  }

  private static parseActionStepsJSON(json: TeacherActionStepsJSON, engine: GeometryEngine): ConstructionStep[] {
    const objectMap = new Map<string, any>();
    const steps: ConstructionStep[] = [];

    json.steps.forEach((stepData, stepIdx) => {
      const animations: any[] = [];
      const actions = stepData.actions || [];

      actions.forEach((act) => {
        const id = act.id || `obj_${stepIdx}_${Math.random().toString(36).substr(2, 6)}`;

        switch (act.draw) {
          case 'point': {
            const pos = act.pos || [200, 200];
            const p = new Point(engine.renderer, pos[0], pos[1], act.text || '', id);
            p.setVisible(false);
            objectMap.set(id, p);
            animations.push(new DrawPoint(p));
            break;
          }
          case 'segment': {
            const p1 = Array.isArray(act.from) ? { x: act.from[0], y: act.from[1] } : { x: 200, y: 400 };
            const p2 = Array.isArray(act.to) ? { x: act.to[0], y: act.to[1] } : { x: 400, y: 400 };
            const seg = new Segment(engine.renderer, p1, p2, false, id);
            seg.setVisible(false);
            objectMap.set(id, seg);
            animations.push(new DrawLine(seg, act.tool === 'ruler' ? engine.ruler : undefined));
            break;
          }
          case 'circle': {
            const center = Array.isArray(act.center) ? { x: act.center[0], y: act.center[1] } : { x: 300, y: 300 };
            const radius = act.radius || 100;
            const circle = new Circle(engine.renderer, center, radius, false, id);
            circle.setVisible(false);
            objectMap.set(id, circle);
            animations.push(new DrawCircle(circle, act.tool === 'compass' ? engine.compass : undefined));
            break;
          }
          case 'arc': {
            const center = Array.isArray(act.center) ? { x: act.center[0], y: act.center[1] } : { x: 300, y: 300 };
            const radius = act.radius || 100;
            const startAngle = act.startAngle || 0;
            const endAngle = act.endAngle || Math.PI / 2;
            const arc = new Arc(engine.renderer, center, radius, startAngle, endAngle, true, false, id);
            arc.setVisible(false);
            objectMap.set(id, arc);
            animations.push(new DrawArc(arc, act.tool === 'compass' ? engine.compass : undefined));
            break;
          }
          case 'label': {
            const pos = act.pos || [200, 200];
            const label = new Label(engine.renderer, { x: pos[0], y: pos[1] }, act.text || '', id);
            label.setVisible(false);
            objectMap.set(id, label);
            animations.push(new Fade(label, true));
            break;
          }
        }
      });

      const stepObj: ConstructionStep = {
        id: `step_${stepIdx + 1}`,
        title: stepData.title || `Step ${stepIdx + 1}`,
        description: stepData.description || '',
        narration: stepData.narration || stepData.title || '',
        duration: stepData.duration || 1500,
        animations,
      };

      if (stepData.voiceoverAudioUrl || stepData.narrationAudioUrl) {
        (stepObj as any).voiceoverAudioUrl = stepData.voiceoverAudioUrl || stepData.narrationAudioUrl;
      }

      steps.push(stepObj);
    });

    return steps;
  }
}
